import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Calendar, CheckCircle, AlertTriangle, Plus, Shield, RefreshCw, X, Building2 } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = (): Record<string, string> => {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
};

interface Company { id: number; name: string; }
interface Certification { id: number; company_id: number; company_name?: string; certificate_number: string; status: string; issue_date: string; expiry_date: string; }
interface SurveillanceAudit { id: number; certification_id: number; audit_type: string; status: string; scheduled_date: string; completed_date?: string; findings?: string; recommendations?: string; next_audit_date?: string; auditor_id: number; }

const SurveillanceAudits: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number>(0);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [audits, setAudits] = useState<SurveillanceAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState({ certificationId: 0, auditType: 'routine', scheduledDate: '' });

  useEffect(() => { loadCompanies(); }, []);
  useEffect(() => { if (selectedCompany) loadCertifications(selectedCompany); else { setCertifications([]); setAudits([]); } }, [selectedCompany]);

  async function loadCompanies() {
    setLoading(true);
    try {
      let res = await fetch('/api/companies', { headers: authHeaders() });
      if (!res.ok) res = await fetch('/api/companies', { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCompanies(data.data || data.companies || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }

  async function loadCertifications(companyId: number) {
    try {
      const res = await fetch(`/api/compliance/certifications/company/${companyId}`, { headers: authHeaders() });
      if (!res.ok) { setCertifications([]); setAudits([]); return; }
      const data = await res.json();
      const certs = data.data || data.certifications || [];
      setCertifications(certs);

      // Load audits for active certifications
      const allAudits: SurveillanceAudit[] = [];
      for (const cert of certs.filter((c: any) => c.status === 'active')) {
        try {
          const auditRes = await fetch(`/api/compliance/certifications/${cert.id}/surveillance-audits`, { headers: authHeaders() });
          if (auditRes.ok) {
            const auditData = await auditRes.json();
            allAudits.push(...(auditData.data || auditData.audits || []));
          }
        } catch { /* skip */ }
      }
      setAudits(allAudits);
    } catch { setCertifications([]); setAudits([]); }
  }

  async function handleSchedule(e: React.FormEvent) {
    e.preventDefault();
    if (!form.certificationId || !form.scheduledDate) { setError('Select a certification and date.'); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`/api/compliance/certifications/${form.certificationId}/surveillance-audits`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ audit_type: form.auditType, scheduled_date: form.scheduledDate, auditor_id: user?.id }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || 'Failed'); }
      setSuccess('Audit scheduled successfully!');
      setShowModal(false);
      setForm({ certificationId: 0, auditType: 'routine', scheduledDate: '' });
      if (selectedCompany) loadCertifications(selectedCompany);
    } catch (err: any) { setError(err.message); }
    setSubmitting(false);
  }

  const statusBadge = (status: string) => {
    const map: Record<string, string> = { scheduled: 'bg-blue-100 text-blue-800', in_progress: 'bg-amber-100 text-amber-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-slate-100 text-slate-600' };
    return <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${map[status] || map.scheduled}`}>{status.replace('_', ' ')}</span>;
  };

  const filtered = audits.filter(a => statusFilter === 'all' || a.status === statusFilter);
  const activeCerts = certifications.filter(c => c.status === 'active');

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="text-blue-600 animate-spin" size={40} />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Surveillance Audits</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm mt-0.5">Schedule and track post-certification surveillance audits</p>
          </div>
          <button onClick={() => setShowModal(true)} disabled={activeCerts.length === 0}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <Plus size={16} /> Schedule Audit
          </button>
        </div>

        {/* Alerts */}
        {success && <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3"><CheckCircle className="text-green-600 shrink-0" size={18} /><p className="text-green-800 font-medium text-sm">{success}</p><button onClick={() => setSuccess(null)} className="ml-auto text-green-600 text-sm">Dismiss</button></div>}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3"><AlertTriangle className="text-red-600 shrink-0" size={18} /><p className="text-red-800 text-sm">{error}</p><button onClick={() => setError(null)} className="ml-auto text-red-600 text-sm">Dismiss</button></div>}

        {/* Company selector */}
        <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Building2 size={15} className="text-blue-600" /> Select Company</label>
          <select value={selectedCompany} onChange={e => setSelectedCompany(Number(e.target.value))}
            className="w-full sm:w-80 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm">
            <option value={0}>Choose a company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Content */}
        {selectedCompany > 0 && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-3xl border border-slate-100 p-4 text-center">
                <p className="text-3xl font-bold text-slate-900">{activeCerts.length}</p>
                <p className="text-xs text-slate-500">Active Certifications</p>
              </div>
              <div className="bg-white rounded-3xl border border-slate-100 p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{audits.filter(a => a.status === 'scheduled').length}</p>
                <p className="text-xs text-slate-500">Scheduled</p>
              </div>
              <div className="bg-white rounded-3xl border border-slate-100 p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{audits.filter(a => a.status === 'in_progress').length}</p>
                <p className="text-xs text-slate-500">In Progress</p>
              </div>
              <div className="bg-white rounded-3xl border border-slate-100 p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{audits.filter(a => a.status === 'completed').length}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2">
              {['all', 'scheduled', 'in_progress', 'completed'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                  {s === 'all' ? 'All' : s.replace('_', ' ')}
                </button>
              ))}
            </div>

            {/* Audit list */}
            <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
              {filtered.length === 0 ? (
                <div className="py-12">
                  <Calendar className="mx-auto text-slate-300 mb-3" size={36} />
                  <p className="text-slate-600 font-medium text-sm">No surveillance audits found</p>
                  <p className="text-xs text-slate-400 mt-1">{activeCerts.length > 0 ? 'Schedule one to maintain certification compliance.' : 'No active certifications for this company.'}</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filtered.map(audit => {
                    const cert = certifications.find(c => c.id === audit.certification_id);
                    return (
                      <div key={audit.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center gap-4">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <Shield size={16} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{cert?.certificate_number || `Cert #${audit.certification_id}`}</p>
                          <p className="text-xs text-slate-500">{audit.audit_type.replace('_', ' ')} &middot; {new Date(audit.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                        </div>
                        {statusBadge(audit.status)}
                        {audit.completed_date && <span className="text-xs text-slate-400 hidden sm:inline">Completed {new Date(audit.completed_date).toLocaleDateString('en-GB')}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Schedule Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl border border-slate-100 max-w-md w-full">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">Schedule Surveillance Audit</h2>
                <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
              </div>
              <form onSubmit={handleSchedule} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Certification</label>
                  <select value={form.certificationId} onChange={e => setForm(f => ({ ...f, certificationId: Number(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value={0}>Select...</option>
                    {activeCerts.map(c => <option key={c.id} value={c.id}>{c.certificate_number}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                  <select value={form.auditType} onChange={e => setForm(f => ({ ...f, auditType: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="routine">Routine</option>
                    <option value="special">Special</option>
                    <option value="follow_up">Follow-up</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input type="date" value={form.scheduledDate} onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 flex items-center gap-2">
                    {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                    {submitting ? 'Scheduling...' : 'Schedule'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveillanceAudits;
